import { TEXT_ROLES } from "../text/typography"
// Palette and metrics shared by the chat LOG (gui/chat_history.ts) and the chat INPUT
// (gui/typing.ts). The two sit in the same corner and have to read as one component, so every
// colour and every measurement either widget uses lives here.
//
// Deliberately the ember HUD's warm dark glass (cf. gui/tutorial_prompt.ts, gui/role_picker.ts,
// gui/command_wheel.ts) rather than a palette of chat's own: the chat log shares the bottom-left
// corner with the readied-item slots and sits under the camp HUD, and a cool slate panel there
// looked like a different game's overlay.

export const CHAT_WIDTH = 320
export const CHAT_CORNER = 8
export const CHAT_PAD_X = 9
export const CHAT_PAD_Y = 6

// Dark glass. The log is a touch more transparent than the input: it is on screen while the
// player is NOT typing, so it has to give the world back.
export const PANEL_FILL = 0x1b1512
export const LOG_PANEL_ALPHA = 0.84
export const INPUT_PANEL_ALPHA = 0.93

// Fake drop shadow: an offset copy of the panel drawn behind it. No filters -- see the budget
// hardware target; a DropShadowFilter here would cost a render target per frame the log is up.
export const SHADOW_COLOR = 0x000000
export const SHADOW_ALPHA = 0.3
export const SHADOW_OFFSET = 2

// Rims. The log is passive (dim bronze); the input is the thing holding the keyboard, so it gets
// the ember rim the rest of the HUD uses for "this is live".
export const RIM_IDLE = 0x6b4f38
export const RIM_ACTIVE = 0xf2a94e

export const TEXT_COLOR = 0xfff3e0
export const CARET_COLOR = 0xf2a94e
export const COMMAND_COLOR = 0xf2c879
export const WARN_COLOR = 0xf2c879
export const DANGER_COLOR = 0xe8745a

export const LOG_FONT_SIZE = TEXT_ROLES.compactCaption.fontSize
export const LOG_LINE_GAP = 3
export const INPUT_FONT_SIZE = TEXT_ROLES.caption.fontSize
export const INPUT_COUNTER_FONT_SIZE = TEXT_ROLES.compactCaption.fontSize
export const INPUT_HEIGHT = 22

// How many lines the log keeps (it was five). The log floats over the world unprompted, so it
// earns its space back by staying short -- and each line can now wrap onto two, which makes a
// taller cap than this reach up into the camp HUD.
export const LOG_MAX_MESSAGES = 6

// The oldest line on screen is dimmed to this; the newest is always full strength. Recency is
// worth more than a tidy block of identical text when the log is glanced at, not read.
export const LOG_OLDEST_ALPHA = 0.5

// Your own name, so you can find yourself in a busy log at a glance.
export const SELF_SPEAKER_COLOR = 0xffd27f

// Everyone else. Warm-leaning so they sit with the panel, but far enough apart in hue that two
// speakers in the same conversation never read as the same person.
const SPEAKER_COLORS = [
    0x8fc9f0, // sky
    0x9fe08f, // leaf
    0xf0a6c8, // rose
    0xc4b3f0, // iris
    0x7fe0d4, // teal
    0xf0b27a, // clay
    0xd6d66b, // straw
    0xe39393, // brick
]

// Stable per-name colour: the same speaker is the same colour for the whole session, and across
// sessions, without anything having to be stored. djb2 over the name, which spreads short names
// (the usual case here) much better than summing char codes.
export const speakerColor = (name: string, isSelf?: boolean): number => {
    if (isSelf) {
        return SELF_SPEAKER_COLOR
    }
    let hash = 5381
    for (let i = 0; i < name.length; i++) {
        hash = ((hash << 5) + hash + name.charCodeAt(i)) >>> 0
    }
    return SPEAKER_COLORS[hash % SPEAKER_COLORS.length]
}
